/**
 * Admin API — services catalog domain.
 *
 * Services catalog (CRUD + relationships/children + supply requirements),
 * supplies + supply inventory, suppliers, units of measure, and regions.
 * Extracted from the AdminService god class in services/auth.ts and
 * converted to the central web API client.
 */

import { webApiClient, unwrap } from '../apiClient';
import { dedup, extractItems, writeThenForget } from './helpers';
import type { Service, ServiceListResponse } from '@turn-connect/shared';

// ---------------------------------------------------------------------------
// Service payload allowlist (TEAM GOTCHA — keep co-located with
// createService/updateService).
//
// buildServicePayload silently drops any field NOT listed in
// servicePayloadKeys, so a new writable backend field that isn't added here
// will appear to save but never reach the API. Only keys in
// nullablePayloadKeys may pass `null` (to clear overrides); null for any
// other key is dropped. Reconcile with the backend UpdateServiceRequest
// whenever fields are added. Do not alter this list casually.
// ---------------------------------------------------------------------------
export const servicePayloadKeys = [
  'name',
  'description',
  'department_id',
  'labor_hours',
  'part_price',
  'misc_supply_price',
  'spiff_amount',
  'additional_information',
  'display_on_invoice',
  'note_required',
  'photo_required',
  'active',
  'task_only',
  'allow_name_change',
  'taxed',
  'is_service_group',
  'tag_ids',
  // Deprecated single-tag fields; the web sends tag_ids.
  'tag_id',
  'clear_tag_id',
  'labor_amount',
  'clear_labor_amount',
  'accounting_item_id',
  'accounting_class_id',
  'estimation_method',
  'max_quantity',
  'bypass_duplicate_check',
  // What work this service produces, when that work is a different service (a
  // PDR inspection producing dent repair). clear_ puts it back to producing
  // its own work — `null` alone would be dropped by the rule above.
  'produces_service_id',
  'clear_produces_service_id',
];

// Keys that should be included even when null (to clear values)
export const nullablePayloadKeys = ['labor_amount'];

// Integer keys. The decimal money/hours fields marshal fine as strings
// (the backend parses them into decimals), but an int field must travel as a
// JSON number or the request fails to bind. A blank value is omitted so the
// backend applies its own default.
export const integerPayloadKeys = ['max_quantity'];

export function buildServicePayload(service: any): Record<string, any> {
  const payload: Record<string, any> = {};
  for (const key of servicePayloadKeys) {
    if (!Object.prototype.hasOwnProperty.call(service, key)) continue;
    const value = service[key];
    // Allow null values for nullable keys (to clear overrides)
    if (value === undefined) continue;
    if (value === null) {
      if (nullablePayloadKeys.includes(key)) {
        payload[key] = null;
      }
      continue;
    }
    if (integerPayloadKeys.includes(key)) {
      const trimmed = typeof value === 'string' ? value.trim() : value;
      if (trimmed === '') continue;
      const n = Math.trunc(Number(trimmed));
      if (!Number.isFinite(n)) continue;
      payload[key] = n;
      continue;
    }
    if (typeof value === 'string') {
      // Allow empty strings for text fields (to clear values)
      payload[key] = value.trim();
    } else {
      payload[key] = value;
    }
  }
  return payload;
}

// ---------------------------------------------------------------------------
// Service Management
// ---------------------------------------------------------------------------

export async function getServices(locationId?: string): Promise<Service[]> {
  const endpoint = locationId ? `/locations/${locationId}/services` : `/services`;
  const json = await unwrap(webApiClient.get<any>(endpoint), 'Failed to fetch services');
  const services: Service[] = extractItems(json, ['services']);
  return services;
}

export async function listServices(
  params: {
    location_id?: string;
    /**
     * Account-scope listing (GET /accounts/{id}/services — account-created
     * services plus every location's catalog). Only used when no location_id
     * is given; a location_id always wins and hits the location endpoint.
     * NOTE: the backend paginates this endpoint (default 50, HARD MAX 1000),
     * so callers wanting a complete account-wide view must drive `search`
     * server-side or page with limit/offset.
     */
    account_id?: string;
    department_id?: string;
    search?: string;
    limit?: number;
    offset?: number;
    include_deleted?: boolean;
    allowed_statuses?: string;
    excluded_statuses?: string;
    tag_ids?: string;
    include_untagged?: boolean;
    include_relationships?: boolean;
    /**
     * 'selection' returns only the fields a PICKER renders. A location holds
     * ~550 services and the full row is ~854 bytes of JSON, roughly half of it
     * columns no picker reads. The location EDITOR omits this — it edits every
     * column, so it needs them.
     */
    view?: 'selection';
    /** Drops organizational containers server-side; no picker can add one. */
    exclude_service_groups?: boolean;
  } = {}
): Promise<ServiceListResponse> {
  const base = params.location_id
    ? `/locations/${params.location_id}/services`
    : params.account_id
      ? `/accounts/${params.account_id}/services`
      : `/services`;
  const q = new URLSearchParams();
  if (params.department_id) q.set('department_id', params.department_id);
  if (params.search) q.set('search', params.search);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  if (params.include_deleted) q.set('include_deleted', 'true');
  if (params.allowed_statuses) q.set('allowed_statuses', params.allowed_statuses);
  if (params.excluded_statuses) q.set('excluded_statuses', params.excluded_statuses);
  if (params.tag_ids) q.set('tag_ids', params.tag_ids);
  if (params.include_untagged) q.set('include_untagged', 'true');
  if (params.include_relationships) q.set('include_relationships', 'true');
  if (params.view) q.set('view', params.view);
  if (params.exclude_service_groups) q.set('exclude_service_groups', 'true');
  const qs = q.toString();
  const endpoint = qs ? `${base}?${qs}` : base;
  // Dedup key mirrors the legacy key, which embedded the absolute URL.
  return dedup(`listServices:${webApiClient.getBaseUrl()}${endpoint}`, async () => {
    const json = await unwrap(webApiClient.get<any>(endpoint), 'Failed to fetch services');

    // Legacy format: just an array - wrap it in ServiceListResponse
    const services: Service[] = extractItems(json, ['services']);

    return {
      items: services,
      services: services,
      total: services.length,
      limit: params.limit ?? 1000,
      offset: params.offset ?? 0,
      has_more: false,
    } as ServiceListResponse;
  });
}

export async function getServiceSupplyRequirements(serviceId: string): Promise<any[]> {
  const json = await unwrap(
    webApiClient.get<any>(`/services/${serviceId}/supply-requirements`),
    'Failed to fetch service supply requirements'
  );
  return extractItems(json, ['requirements']);
}

export async function updateServiceSupplyRequirements(
  serviceId: string,
  requirements: Array<{
    supply_id: string;
    estimated_quantity?: number;
    maximum_quantity?: number;
    is_required?: boolean;
    notes?: string;
  }>
): Promise<any[]> {
  const body = { requirements } as any;
  const json = await unwrap(webApiClient.put<any>(`/services/${serviceId}/supply-requirements`, body), 'Failed to update service supplies');
  return extractItems(json, ['requirements']);
}

/** Every cached service list: a write below makes them all stale. */
const SERVICE_LISTS = 'listServices:';

export async function createService(locationId: string, service: any): Promise<any> {
  if (!locationId) {
    throw new Error('Location ID is required to create service');
  }
  const body = buildServicePayload(service);
  return writeThenForget(SERVICE_LISTS, () =>
    unwrap(webApiClient.post<any>(`/locations/${locationId}/services`, body), 'Failed to create service')
  );
}

export async function updateService(locationId: string | null | undefined, serviceId: string | number, service: any): Promise<any> {
  const trimmedLocationId = String(locationId ?? '').trim();
  const trimmedServiceId = String(serviceId ?? '').trim();

  if (!trimmedLocationId) {
    throw new Error('Location ID is required to update service');
  }
  if (!trimmedServiceId) {
    throw new Error('Service ID is required to update service');
  }

  const body = buildServicePayload(service);
  const endpoint = `/locations/${encodeURIComponent(trimmedLocationId)}/services/${encodeURIComponent(trimmedServiceId)}`;

  const responseData = await writeThenForget(SERVICE_LISTS, () =>
    unwrap(webApiClient.put<any>(endpoint, body), 'Failed to update service')
  );
  return responseData ?? {};
}

export async function deleteService(serviceId: string): Promise<void> {
  await writeThenForget(SERVICE_LISTS, () => unwrap(webApiClient.delete<any>(`/services/${serviceId}`), 'Failed to delete service'));
}

export async function undeleteService(serviceId: string): Promise<void> {
  await writeThenForget(SERVICE_LISTS, () =>
    unwrap(webApiClient.post<any>(`/services/${serviceId}/undelete`), 'Failed to restore service')
  );
}

// ---------------------------------------------------------------------------
// Service Relationships (Children) — service groups are services with
// is_service_group=true plus child relationships managed here.
// ---------------------------------------------------------------------------

export async function getServiceRelationships(serviceId: string): Promise<any[]> {
  const json = await unwrap(
    webApiClient.get<any>(`/services/${encodeURIComponent(serviceId)}/relationships`),
    'Failed to fetch service relationships'
  );
  // API returns array of ServiceRelationship objects directly or in a wrapper
  const list: any[] = Array.isArray(json)
    ? json
    : (extractItems(json, ['children', 'child_services', 'relationships', 'items', 'data']) as any[]);
  return (list as any[]).map((r: any) => {
    const childService = r.child_service || r.related_service || r.service;
    return {
      id: String(r.id ?? r.relationship_id ?? r.rel_id ?? ''),
      parent_service_id: r.parent_service_id ?? r.service_id ?? r.host_service_id ?? undefined,
      child_service_id: r.child_service_id ?? r.related_service_id ?? r.sub_service_id ?? childService?.id ?? undefined,
      quantity: r.quantity ?? undefined,
      order_position: r.order_position ?? undefined,
      is_required: r.is_required ?? undefined,
      notes: r.notes ?? undefined,
      related_service: childService,
    };
  });
}

export async function addServiceRelationship(parentServiceId: string, childServiceId: string): Promise<any> {
  const body = { child_service_id: childServiceId } as any;
  const data = await writeThenForget(SERVICE_LISTS, () =>
    unwrap(
      webApiClient.post<any>(`/services/${encodeURIComponent(parentServiceId)}/relationships`, body),
      'Failed to add service relationship'
    )
  );
  return data ?? {};
}

export async function addServiceRelationshipsBatch(
  parentServiceId: string,
  childServices: Array<{
    child_service_id: string;
    quantity?: number;
    order_position?: number;
    is_required?: boolean;
    notes?: string;
  }>
): Promise<any> {
  const body = { child_services: childServices };
  const data = await writeThenForget(SERVICE_LISTS, () =>
    unwrap(
      webApiClient.post<any>(`/services/${encodeURIComponent(parentServiceId)}/relationships/batch`, body),
      'Failed to add service relationships'
    )
  );
  return data ?? {};
}

export async function deleteServiceRelationship(parentServiceId: string, relationshipId: string): Promise<void> {
  await writeThenForget(SERVICE_LISTS, () =>
    unwrap(
      webApiClient.delete<any>(`/services/${encodeURIComponent(parentServiceId)}/relationships/${encodeURIComponent(relationshipId)}`),
      'Failed to delete service relationship'
    )
  );
}

// ---------------------------------------------------------------------------
// Inventory Management (supplies, supply types, units of measure)
// ---------------------------------------------------------------------------

export async function getUnitsOfMeasure(): Promise<any[]> {
  const json = await unwrap(webApiClient.get<any>(`/units-of-measure`), 'Failed to fetch units of measure');
  return extractItems(json, ['units']);
}

export async function getSupplies(locationId: string): Promise<any[]> {
  const json = await unwrap(webApiClient.get<any>(`/locations/${locationId}/supplies`), 'Failed to fetch supplies');
  return extractItems(json, ['supplies']);
}

export async function getSupplyTypes(locationId: string): Promise<any[]> {
  const json = await unwrap(webApiClient.get<any>(`/locations/${locationId}/supply-types`), 'Failed to fetch supply types');
  return extractItems(json, ['supply_types', 'types', 'data']);
}

export async function createSupply(locationId: string, supply: any): Promise<any> {
  return unwrap(webApiClient.post<any>(`/locations/${locationId}/supplies`, supply), 'Failed to create supply');
}

export async function updateSupply(supplyId: string, supply: any): Promise<any> {
  return unwrap(webApiClient.put<any>(`/supplies/${supplyId}`, supply), 'Failed to update supply');
}

export async function updateSupplyInventory(supplyId: string, newQuantity: number, userId: string, notes?: string): Promise<any> {
  const payload = {
    supply_id: supplyId,
    created_by_user_id: userId,
    transaction_type: 'adjustment',
    new_quantity: newQuantity,
    notes: notes || 'Updated via UI',
  };

  return unwrap(webApiClient.put<any>(`/supplies/${supplyId}/inventory`, payload), 'Failed to update supply inventory');
}

export async function deleteSupply(supplyId: string): Promise<void> {
  await unwrap(webApiClient.delete<any>(`/supplies/${supplyId}`), 'Failed to delete supply');
}

export async function attachSupplyToService(serviceId: string, supplyId: string): Promise<void> {
  await unwrap(webApiClient.post<any>(`/services/${serviceId}/supplies/${supplyId}/attach`), 'Failed to attach supply to service');
}

export async function detachSupplyFromService(serviceId: string, supplyId: string): Promise<void> {
  await unwrap(webApiClient.delete<any>(`/services/${serviceId}/supplies/${supplyId}`), 'Failed to detach supply from service');
}

// ---------------------------------------------------------------------------
// Supplier Management
// ---------------------------------------------------------------------------

export async function getSuppliers(locationId: string): Promise<any[]> {
  const json = await unwrap(webApiClient.get<any>(`/locations/${locationId}/suppliers`), 'Failed to fetch suppliers');
  return extractItems(json, ['suppliers']);
}

export async function createSupplier(locationId: string, supplier: any): Promise<any> {
  return unwrap(webApiClient.post<any>(`/locations/${locationId}/suppliers`, supplier), 'Failed to create supplier');
}

export async function updateSupplier(supplierId: string, supplier: any): Promise<any> {
  return unwrap(webApiClient.put<any>(`/suppliers/${supplierId}`, supplier), 'Failed to update supplier');
}

export async function deleteSupplier(supplierId: string): Promise<void> {
  await unwrap(webApiClient.delete<any>(`/suppliers/${supplierId}`), 'Failed to delete supplier');
}

// ---------------------------------------------------------------------------
// Aggregate
// ---------------------------------------------------------------------------

export const catalogAdmin = {
  // service payload allowlist (team gotcha — keep exported)
  servicePayloadKeys,
  nullablePayloadKeys,
  buildServicePayload,
  // services catalog
  getServices,
  listServices,
  createService,
  updateService,
  deleteService,
  undeleteService,
  // service relationships / children / groups
  getServiceRelationships,
  addServiceRelationship,
  addServiceRelationshipsBatch,
  deleteServiceRelationship,
  // service supply requirements
  getServiceSupplyRequirements,
  updateServiceSupplyRequirements,
  attachSupplyToService,
  detachSupplyFromService,
  // supplies + inventory
  getUnitsOfMeasure,
  getSupplies,
  getSupplyTypes,
  createSupply,
  updateSupply,
  updateSupplyInventory,
  deleteSupply,
  // suppliers
  getSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  // regions
};
